import { ProfileCountsBadge } from '@/features/candidate-profile/profile-section-counts'
import { InterviewBookingBadge } from '@/features/interview-scheduling/interview-booking-badge'
import { Glyphs, logger, TimeAgo, Tooltip } from '@/ui'
import { CandidateModel } from '@/ui'
import { getScoreBackgroundColor } from '@/ui'
import type { CampaignCandidatesViewModel } from '@/ui'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { AlertTriangleIcon, CheckSquareIcon, MailIcon, MessageCircleIcon } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CandidateAttachmentsBadge } from './attachments-badge'
import type { SortOption } from './campaign-candidates-toolbar'
import { NotesBadge } from './notes-badge'
import { StageDeletionModal } from './stage-deletion-modal'
import { KanbanBoard } from './swimlanes/kanban'
import type { Stage } from './swimlanes/kanban'

export interface CampaignCandidatesKanbanProps {
	view: CampaignCandidatesViewModel['view']
	sortOption: SortOption
	onCandidateMove: (candidateIds: string[], stageId: string) => void
	onCandidateClick: (candidateId: string) => void
	editMode?: boolean
	onStageRename?: (stageId: string, newName: string) => void
	onStageAdd?: (afterStageId: string, name: string) => void
	onStageDelete?: (stageId: string, migrateCandidatesTo?: string) => void | Promise<void>
	onStagesReorder?: (stages: Stage[]) => void
	isDeleting?: boolean
}

export function CampaignCandidatesKanban({
	view,
	sortOption,
	onCandidateMove,
	onCandidateClick,
	editMode = false,
	onStageRename,
	onStageAdd,
	onStageDelete,
	onStagesReorder,
	isDeleting = false,
}: CampaignCandidatesKanbanProps) {
	const { t } = useTranslation('common')
	const isWhatsappEnabled = useWhatsAppEnabled()

	// State for deletion modal
	const [deletionModalStage, setDeletionModalStage] = useState<Stage | null>(null)
	const [isDeletingLocal, setIsDeletingLocal] = useState(false)

	// Transform stages to kanban format - stages come pre-ordered from API
	const kanbanStages: Stage[] = useMemo(() => {
		return view.stages.map((stage: { id: string; name: string; locked?: boolean }, index: number) => ({
			id: stage.id,
			name: stage.name,
			order: index,
			locked: stage.locked,
		}))
	}, [view.stages])

	// Filter out rejected stage from main columns (it's shown in the drawer)
	const nonRejectedStages = kanbanStages.filter((stage) => stage.name !== 'Rejected')

	// Find the rejected stage (shown in drawer, must be appended when reordering)
	const rejectedStage = kanbanStages.find((stage) => stage.name === 'Rejected')
	const rejectedStageId = rejectedStage?.id

	// Handle stages reorder - append rejected stage back before sending to API
	const handleStagesReorder = useCallback((reorderedStages: Stage[]) => {
		if (!onStagesReorder) { return }
		// Add rejected stage back at the end
		const fullStages = rejectedStage
			? [...reorderedStages, rejectedStage]
			: reorderedStages
		onStagesReorder(fullStages)
	}, [onStagesReorder, rejectedStage])

	// Handle stage delete - show modal if stage has candidates
	const handleStageDeleteClick = useCallback(async (stageId: string) => {
		const stage = kanbanStages.find(s => s.id === stageId)
		if (!stage) { return }

		const candidateCount = view.candidateCounts[stageId] ?? 0

		if (candidateCount > 0) {
			setDeletionModalStage(stage)
		} else {
			try {
				await onStageDelete?.(stageId)
			} catch (error) {
				logger.error(error, {
					category: 'pipeline',
					data: { stageId, stageName: stage.name, action: 'delete' },
				})
			}
		}
	}, [kanbanStages, view.candidateCounts, onStageDelete])

	const handleDeleteWithMigration = useCallback(async (migrateTo: string) => {
		if (!deletionModalStage) { return }

		setIsDeletingLocal(true)
		try {
			await onStageDelete?.(deletionModalStage.id, migrateTo)
			setDeletionModalStage(null)
		} catch (error) {
			logger.error(error, {
				category: 'pipeline',
				data: {
					stageId: deletionModalStage.id,
					stageName: deletionModalStage.name,
					migrateCandidatesTo: migrateTo,
					action: 'deleteWithMigration',
				},
			})
		} finally {
			setIsDeletingLocal(false)
		}
	}, [deletionModalStage, onStageDelete])

	// Get available target stages for migration (all stages except the one being deleted)
	const migrationTargetStages = useMemo(() => {
		if (!deletionModalStage) { return [] }
		return kanbanStages.filter(s => s.id !== deletionModalStage.id)
	}, [kanbanStages, deletionModalStage])

	const handleCandidateMove = useCallback((candidateIds: string[], targetStageId: string) => {
		// Bulk moves are tracked server-side via "Candidates: Bulk Moved"
		if (candidateIds.length === 1) {
			const candidate = view.candidates.find(c => c.id === candidateIds[0])
			const fromStageName = kanbanStages.find(s => s.id === candidate?.stageId)?.name
			const targetStageName = kanbanStages.find(s => s.id === targetStageId)?.name

			logger.track('candidates:candidate_move_stage', {
				data: {
					trigger: 'swimlane_drag',
					candidate_id: candidateIds[0],
					from_stage: fromStageName,
					to_stage: targetStageName,
				},
			})
		}

		onCandidateMove(candidateIds, targetStageId)
	}, [onCandidateMove, kanbanStages, view.candidates])

	// Sort candidates based on sortOption
	const sortFn = useCallback((a: CandidateModel, b: CandidateModel) => {
		const [field, direction] = sortOption.split('-') as ['name' | 'score' | 'applied', 'asc' | 'desc']
		const dir = direction === 'asc' ? 1 : -1

		switch (field) {
			case 'name': {
				const nameA = a.name?.toLowerCase() ?? ''
				const nameB = b.name?.toLowerCase() ?? ''
				return nameA.localeCompare(nameB) * dir
			}
			case 'score': {
				const scoreA = a.score?.percentage ?? -1
				const scoreB = b.score?.percentage ?? -1
				return (scoreA - scoreB) * dir
			}
			case 'applied': {
				const timeA = a.lastSubmittedAt?.getTime() ?? 0
				const timeB = b.lastSubmittedAt?.getTime() ?? 0
				return (timeA - timeB) * dir
			}
			default:
				return 0
		}
	}, [sortOption])

	// Render candidate card content
	const renderCard = useCallback(
		(candidate: CandidateModel) => {
			const answeredCount = (candidate.additionalQuestions ?? []).filter(
				(question) => question.questionType !== 'file',
			).length

			return (
				<div>
					<div className="flex items-center gap-2 mb-1">
						<h3 className="ph-no-capture font-semibold text-[15px] text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap flex-1">
							{candidate.name}
						</h3>
						{candidate.hasOrphanedStage && (
							<Tooltip content="This candidate's previous stage no longer exists. Please assign them to a new stage.">
								<span
									className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex-shrink-0"
									aria-label={t('pipeline.orphanedStage')}
								>
									<AlertTriangleIcon className="size-3" aria-hidden="true" />
								</span>
							</Tooltip>
						)}
						{candidate.hasUnreadEmails && candidate.unreadEmailCount && candidate.unreadEmailCount > 0 && (
							<Tooltip
								content={`${candidate.unreadEmailCount} unread email${
									candidate.unreadEmailCount > 1 ? 's' : ''
								}`}
							>
								<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
									<MailIcon className="size-3" />
									{candidate.unreadEmailCount}
								</span>
							</Tooltip>
						)}
						{isWhatsappEnabled && candidate.hasUnreadWhatsApp && candidate.unreadWhatsAppCount
							&& candidate.unreadWhatsAppCount > 0 && (
							<Tooltip
								content={`${candidate.unreadWhatsAppCount} unread WhatsApp message${
									candidate.unreadWhatsAppCount > 1 ? 's' : ''
								}`}
							>
								<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full flex-shrink-0">
									<MessageCircleIcon className="size-3" />
									{candidate.unreadWhatsAppCount}
								</span>
							</Tooltip>
						)}
						{candidate.notesCount != null && candidate.notesCount > 0 && (
							<NotesBadge count={candidate.notesCount} shrink />
						)}
						<InterviewBookingBadge candidate={candidate} />
					</div>
					<p className="ph-no-capture text-[13px] text-gray-600 mb-2 overflow-hidden text-ellipsis whitespace-nowrap">
						{candidate.email}
					</p>
					{candidate.phone && (
						<p className="ph-no-capture text-xs text-gray-400 mt-1 overflow-hidden text-ellipsis whitespace-nowrap">
							{candidate.phone}
						</p>
					)}
					<div className="flex flex-col gap-2 mt-2">
						{candidate.score && (
							<div>
								{candidate.disqualificationReason
									? (
										<Tooltip content={candidate.disqualificationReason}>
											<div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white">
												<span className="font-medium text-xs">
													DQ (<span>{Math.round(candidate.score.percentage)}%</span>)
												</span>
											</div>
										</Tooltip>
									)
									: (
										<div
											className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
											style={{
												backgroundColor: getScoreBackgroundColor(candidate.score.percentage),
											}}
										>
											<span className="font-medium text-xs">
												{Math.round(candidate.score.percentage)}%
											</span>
										</div>
									)}
							</div>
						)}

						<div className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
							{candidate.lastSubmittedAt && (
								<Tooltip
									content={
										<>
											{t('pipeline.lastApplied')} <TimeAgo date={candidate.lastSubmittedAt} ago />
										</>
									}
								>
									<div className="text-xs text-gray-500 flex items-center gap-1 py-1 pr-2">
										<Glyphs.micro.IntoLaptopIcon />
										<TimeAgo date={candidate.lastSubmittedAt} short />
									</div>
								</Tooltip>
							)}
							{candidate.submissionCount != null && candidate.submissionCount > 1 && (
								<Tooltip content={t('pipeline.appliedTimes', { count: candidate.submissionCount })}>
									<span
										className="px-2 py-0.5 rounded-xl text-xs font-bold inline-flex items-center justify-center min-w-[24px] bg-amber-100 text-amber-800"
										aria-label={t('pipeline.appliedTimes', { count: candidate.submissionCount })}
									>
										{candidate.submissionCount}×
									</span>
								</Tooltip>
							)}
							{answeredCount > 0 && (
								<Tooltip content={t('pipeline.answeredQuestions', { count: answeredCount })}>
									<span
										className="px-2 py-0.5 rounded-xl text-xs font-bold inline-flex items-center gap-1 justify-center min-w-[24px] bg-purple-800/20 text-purple-900"
										aria-label={t('pipeline.answeredQuestions', { count: answeredCount })}
									>
										<CheckSquareIcon className="size-3" aria-hidden="true" />
										{answeredCount}
									</span>
								</Tooltip>
							)}
							<CandidateAttachmentsBadge files={candidate.files} />
							<ProfileCountsBadge counts={candidate.profileCounts} />
						</div>
					</div>
				</div>
			)
		},
		[isWhatsappEnabled, t],
	)

	return (
		<>
			<KanbanBoard<CandidateModel>
				candidates={view.candidates}
				stages={nonRejectedStages}
				renderCard={renderCard}
				sortFn={sortFn}
				onCandidateMove={handleCandidateMove}
				onStagesChange={handleStagesReorder}
				getCandidateId={(candidate) => candidate.id}
				getCandidateStage={(candidate) => candidate.stageId}
				onCardClick={(candidate) => onCandidateClick(candidate.id)}
				getCandidateSelectLabel={(candidate) => `${t('candidates.selectCandidate')}: ${candidate.name}`}
				rejectedStageId={rejectedStageId}
				editMode={editMode}
				onStageRename={onStageRename}
				onStageAdd={onStageAdd}
				onStageDelete={handleStageDeleteClick}
			/>
			<StageDeletionModal
				isOpen={!!deletionModalStage}
				onClose={() => setDeletionModalStage(null)}
				stageName={deletionModalStage?.name ?? ''}
				candidateCount={deletionModalStage ? (view.candidateCounts[deletionModalStage.id] ?? 0) : 0}
				targetStages={migrationTargetStages}
				candidateCounts={view.candidateCounts}
				onConfirm={handleDeleteWithMigration}
				isDeleting={isDeleting || isDeletingLocal}
			/>
		</>
	)
}
